import { useDecodedAccessToken } from '@b-stock/fe-core/bstock-next/components'
import {
  isLotUnmanifested,
  useFlag,
} from '@b-stock/fe-core/bstock-next/helpers'
import { type OrchestratedListingDetails } from '@b-stock/fe-core/bstock-next/queries'

import { useIsMobileTableLayout } from '@/components/listings/shared/useIsMobileTableLayout'
import { FeatureFlags } from '@/helpers/featureFlags'
import { shouldShowManifestSection } from '@/helpers/listing/shouldShowManifestSection'

import { useManifestTableTracking } from '../hooks/useManifestTableTracking'
import {
  ScrollIntoViewId,
  useScrollIntoView,
} from '../ScrollIntoView/ScrollIntoViewContext'
import { ListingManifestAgGrid } from './ListingManifestAgGrid'
import { useMobileScrollPastManifestTracking } from './tracking/useMobileScrollPastManifestTracking'
import { Unmanifested } from './Unmanifested'

export const ListingManifestSection = ({
  listing,
}: {
  listing: OrchestratedListingDetails
}) => {
  const isLoggedIn = !!useDecodedAccessToken()?.personId
  const { refs } = useScrollIntoView()
  const isMobile = useIsMobileTableLayout()
  const { onManifestMobileScrollPastManifest } =
    useManifestTableTracking(listing)

  // [BP-2378] A live read is fine here — this only gates an analytics
  // observer, not rendering/datasource state, so it doesn't need BP-2376's
  // mount-freeze treatment (see FullManifestPanel).
  const isInfiniteScrollEnabled = useFlag(
    FeatureFlags.ENABLE_HP_MANIFEST_INFINITE_SCROLL
  )

  const isManifested = listing.inventoryPreview
    ? listing.inventoryPreview.length > 0
    : !isLotUnmanifested(listing.lot)

  // [BP-2378] The scroll-trap only exists in exactly this combination — see
  // ManifestGrid's isInlineInfiniteScroll (guests always get the small,
  // content-sized preview, never a bounded/scrollable box).
  useMobileScrollPastManifestTracking({
    containerRef: refs[ScrollIntoViewId.MANIFEST],
    enabled: isManifested && isInfiniteScrollEnabled && isMobile && isLoggedIn,
    onScrollPast: onManifestMobileScrollPastManifest,
  })

  if (!shouldShowManifestSection(listing)) {
    return null
  }

  return (
    <div ref={refs[ScrollIntoViewId.MANIFEST]}>
      {!isManifested ? (
        <Unmanifested />
      ) : (
        <ListingManifestAgGrid isLoggedIn={isLoggedIn} listing={listing} />
      )}
    </div>
  )
}
